import { getSDK } from '../../shared/functions/sdk.js'
import { loginAll, logoutAll } from '../../shared/governanceBridge.js'
import { readGovernanceClaims } from '../../shared/functions/diagnoseAuth.js'

export const useRealAuth = false

// Detects whether ANY Supabase project has a live session. Per-project
// tokens are namespaced by ref (sb-<ref>-auth-token), so a user signed
// into governance and financials has two separate entries — either
// presence is enough to consider the user signed in locally.
export const hasSessionToken = () => {
  if (typeof window === 'undefined') return false
  // Symbols SDK access token — primary signal when SDK auth (not Supabase
  // federation) seeded the session. Without this, /tickets and other
  // workspace-project routes bounce to /login even though the user is
  // signed in via the Symbols SDK.
  try {
    const sym = window.localStorage.getItem('symbols_access_token')
    if (sym) {
      const exp = +window.localStorage.getItem('symbols_expires_at')
      if (!exp || exp > Date.now()) return true
    }
  } catch (e) { /* localStorage may throw in some sandbox contexts */ }
  // Supabase federation tokens — sb-<ref>-auth-token shape.
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i)
    if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) return true
  }
  return false
}

// Supabase-JS asynchronously parses URL-hash tokens from magic-link /
// OAuth / recovery callbacks into localStorage. If we bounce to /login
// before that happens, a valid sign-in lands on the login page and
// the tokens in the hash are lost.
const _hasHashSession = () => {
  if (typeof window === 'undefined') return false
  const h = window.location.hash || ''
  return h.includes('access_token=') && h.includes('refresh_token=')
}

export const redirectIfUnauthed = ({
  loginPath = '/login',
  homePath = '/',
  publicPathPrefixes = []
} = {}) => {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  if (publicPathPrefixes.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?') || path === p)) return
  if (_hasHashSession()) return
  if (!hasSessionToken() && path !== loginPath) {
    window.location.replace(loginPath)
  } else if (hasSessionToken() && path === loginPath) {
    window.location.replace(homePath)
  }
}

// Subscribe to auth state changes. Accepts two callbacks:
//   onSignIn(user)            — fired once per new user session (new id)
//   onWorkspaceChange(claims) — fired when active_workspace_id changes
//                               for the same user (e.g. org switch).
//                               NOT called on initial sign-in (onSignIn
//                               covers that path).
//
// Both are optional. Returns an unsubscribe function (sync — see the
// cancel-token shim below for the SDK-proxy reason).
//
// SDK's onAuthStateChange mirrors Supabase's (event, session) shape so
// the callback body reads identical to the Supabase implementation. The
// session.user.app_metadata claim shape is preserved by the bridge.
export const subscribeAuthSession = (root, onSignIn, onWorkspaceChange) => {
  if (!root) return () => {}
  let lastUserId = null
  let lastActiveWorkspaceId = null
  let lastClaimsKey = null
  const handler = (_event, session) => {
    const u = session?.user
    const govClaims = readGovernanceClaims()
    const nextEmail = u?.email || ''
    const nextId = u?.id || u?._id || ''
    const nextSignedIn = !!u || !!govClaims.sub
    // The SDK's `_currentUser` (Mongo) doesn't carry app_metadata; the
    // governance JWT is the source of orgs / active_workspace_id / role.
    // Merge sdk app_metadata first so federation claims win on conflict.
    const nextClaims = { ...(u?.app_metadata || {}), ...govClaims }
    const nextActiveWs = nextClaims?.active_workspace_id || ''
    const userChanged =
      root.signedIn !== nextSignedIn ||
      root.currentUserEmail !== nextEmail ||
      root.currentUserId !== nextId
    // Full-payload diff so TOKEN_REFRESHED ticks that re-mint an
    // identical claim set are no-ops, but ANY claim change (orgs,
    // permissions, workspaces, tier, role, ...) still propagates.
    // Stringify is ~50µs on a 2kB JWT vs the cost of stale role/perm
    // gates — correctness wins.
    const claimsKey = JSON.stringify(nextClaims)
    const claimsChanged = claimsKey !== lastClaimsKey
    // Derive shell-scope state from claims so the OrgSwitcher / shell chrome
    // doesn't have to wait on a separate `/orgs` round-trip — the JWT
    // already carries the user's org membership + active workspace.
    const orgsList = Array.isArray(nextClaims.orgs) ? nextClaims.orgs : []
    const workspacesList = Array.isArray(nextClaims.workspaces) ? nextClaims.workspaces : []
    const activeWorkspace = workspacesList.find((w) => w.id === nextActiveWs) || null
    const activeOrgId = activeWorkspace?.org_id || nextClaims.workspace_org_id || null
    const activeOrg = activeOrgId ? orgsList.find((o) => o.id === activeOrgId) : (orgsList[0] || null)
    if (userChanged) {
      root.update({
        signedIn: nextSignedIn,
        currentUserEmail: nextEmail,
        currentUserId: nextId,
        jwtClaims: nextClaims,
        orgs: orgsList,
        workspaces: workspacesList,
        org: activeOrg,
        workspace: activeWorkspace
      })
      lastClaimsKey = claimsKey
    } else if (nextSignedIn && claimsChanged) {
      root.update({
        jwtClaims: nextClaims,
        orgs: orgsList,
        workspaces: workspacesList,
        org: activeOrg,
        workspace: activeWorkspace
      })
      lastClaimsKey = claimsKey
    }
    if (nextId && nextId !== lastUserId) {
      // New sign-in: fire onSignIn and record the starting workspace.
      lastUserId = nextId
      lastActiveWorkspaceId = nextActiveWs
      if (typeof onSignIn === 'function') onSignIn({ id: nextId, email: nextEmail })
    } else if (nextId && nextActiveWs && nextActiveWs !== lastActiveWorkspaceId) {
      // Same user, different active workspace — workspace switch just landed.
      // Fire onWorkspaceChange so callers can re-load workspace-scoped data
      // without a full page reload.
      lastActiveWorkspaceId = nextActiveWs
      if (typeof onWorkspaceChange === 'function') onWorkspaceChange(nextClaims)
    } else if (!nextId) {
      lastUserId = null
      lastActiveWorkspaceId = null
    }
  }
  // The SDK proxy in shared/functions/sdk.js wraps method calls so they
  // auto-await SDK init — `getSDK().onAuthStateChange(cb)` therefore
  // returns Promise<unsub> instead of a sync unsub. Use a cancel-token
  // shim so callers (workspace-project app onCreate) can safely call
  // the returned unsub from onRemove without storing a Promise.
  let unsub = null
  let cancelled = false
  Promise.resolve(getSDK().onAuthStateChange(handler))
    .then((sub) => {
      if (cancelled) {
        if (typeof sub === 'function') { try { sub() } catch (_) {} }
        return
      }
      unsub = typeof sub === 'function' ? sub : null
    })
    .catch(() => {})
  return () => {
    cancelled = true
    if (typeof unsub === 'function') { try { unsub() } catch (_) {} }
  }
}

// Async because the SDK proxy makes `getSDK().getSession()` return a
// Promise (init-wait wrapper). Callers must await — there are no
// existing call sites today, but exported for completeness.
export const getSessionEmail = async () => {
  const session = await getSDK().getSession()
  return session?.user?.email || null
}

// Log in via the federation layer — governance always, plus any
// configured extensions (financials once its bridge is deployed).
// Return shape matches Supabase's signInWithPassword so existing callers
// (AppLoginCard in shared-gov) don't need to change.
export const signInWithPassword = async function (email, password) {
  const { ok, results, error } = await loginAll({ email, password })
  if (!ok) {
    return {
      data: { user: null, session: null },
      error: { message: error?.message || 'Sign-in failed', status: error?.status }
    }
  }
  // SDK session mirrors the Supabase shape (access_token + user) so the
  // login UI's downstream readers (AppLoginCard) keep working unchanged.
  // The SDK proxy makes getSession() return a Promise — await it.
  const session = await getSDK().getSession()
  return {
    data: {
      user: session?.user || null,
      session: session || null,
      projects: results
    },
    error: null
  }
}

export const signOut = async function () {
  await logoutAll()
}
